import { create } from 'zustand'
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  doc,
  query,
  orderBy,
  Timestamp,
  serverTimestamp,
  where,
} from 'firebase/firestore'
import { db } from '../config/firebase'
import {
  CommunityLink,
  CommunityLinkCategory,
  CommunityLinkGender,
  CreateCommunityLinkInput,
  UpdateCommunityLinkInput,
} from '../models/CommunityLink'

interface CommunityLinkState {
  links: CommunityLink[]
  loading: boolean
  error: string | null
  categoryFilter: CommunityLinkCategory | 'all'
  countryFilter: string | null
  genderFilter: CommunityLinkGender | null
  setCategoryFilter: (category: CommunityLinkCategory | 'all') => void
  setCountryFilter: (country: string | null) => void
  setGenderFilter: (gender: CommunityLinkGender | null) => void
  fetchLinks: () => Promise<void>
  addLink: (input: CreateCommunityLinkInput) => Promise<void>
  updateLink: (id: string, input: UpdateCommunityLinkInput) => Promise<void>
  deleteLink: (id: string) => Promise<void>
  toggleActive: (id: string, isActive: boolean) => Promise<void>
}

export const useCommunityLinkStore = create<CommunityLinkState>((set, get) => ({
  links: [],
  loading: false,
  error: null,
  categoryFilter: 'all',
  countryFilter: null,
  genderFilter: null,

  setCategoryFilter: (category) => {
    set({ categoryFilter: category })
    get().fetchLinks()
  },

  setCountryFilter: (country) => {
    set({ countryFilter: country })
    get().fetchLinks()
  },

  setGenderFilter: (gender) => {
    set({ genderFilter: gender })
    get().fetchLinks()
  },

  fetchLinks: async () => {
    set({ loading: true, error: null })
    try {
      const { categoryFilter } = get()
      let q
      if (categoryFilter === 'all') {
        q = query(collection(db, 'community_links'), orderBy('category'), orderBy('order', 'asc'))
      } else {
        q = query(
          collection(db, 'community_links'),
          where('category', '==', categoryFilter),
          orderBy('order', 'asc')
        )
      }
      const snapshot = await getDocs(q)

      const { countryFilter, genderFilter } = get()
      let links: CommunityLink[] = snapshot.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          title: data.title || '',
          description: data.description || '',
          url: data.url || '',
          imageUrl: data.imageUrl || undefined,
          platform: data.platform || 'website',
          category: data.category || 'world_community',
          order: data.order ?? 0,
          isActive: data.isActive ?? true,
          country: data.country || undefined,
          gender: data.gender || undefined,
          createdAt: data.createdAt ? (data.createdAt as Timestamp).toDate() : new Date(),
          updatedAt: data.updatedAt ? (data.updatedAt as Timestamp).toDate() : undefined,
        }
      })

      // Client-side filters for country/gender
      if (countryFilter) {
        links = links.filter((l) => l.country === countryFilter)
      }
      if (genderFilter) {
        links = links.filter((l) => l.gender === genderFilter)
      }

      set({ links, loading: false })
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : '載入社群連結失敗',
      })
    }
  },

  addLink: async (input: CreateCommunityLinkInput) => {
    set({ loading: true, error: null })
    try {
      const linksInCategory = get().links.filter((l) => l.category === input.category)
      const addData: Record<string, unknown> = {
        ...input,
        order: input.order ?? linksInCategory.length,
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }
      // Strip empty/null optional fields to keep doc structure consistent with updateLink
      if (!addData.imageUrl) delete addData.imageUrl
      if (!addData.country) delete addData.country
      if (!addData.gender) delete addData.gender
      await addDoc(collection(db, 'community_links'), addData)
      await get().fetchLinks()
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : '新增社群連結失敗',
      })
      throw error
    }
  },

  updateLink: async (id: string, input: UpdateCommunityLinkInput) => {
    set({ loading: true, error: null })
    try {
      const updateData: Record<string, unknown> = { updatedAt: serverTimestamp() }
      for (const [key, value] of Object.entries(input)) {
        if (value === undefined || value === '' || value === null) {
          updateData[key] = deleteField()
        } else {
          updateData[key] = value
        }
      }
      await updateDoc(doc(db, 'community_links', id), updateData)
      await get().fetchLinks()
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : '更新社群連結失敗',
      })
      throw error
    }
  },

  deleteLink: async (id: string) => {
    set({ loading: true, error: null })
    try {
      await deleteDoc(doc(db, 'community_links', id))
      await get().fetchLinks()
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : '刪除社群連結失敗',
      })
      throw error
    }
  },

  toggleActive: async (id: string, isActive: boolean) => {
    set({ loading: true, error: null })
    try {
      await updateDoc(doc(db, 'community_links', id), {
        isActive,
        updatedAt: serverTimestamp(),
      })
      await get().fetchLinks()
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : '更新狀態失敗',
      })
      throw error
    }
  },
}))
